// ─── The Google Picker, in one place ──────────────────────────────────────────
// Both Drive surfaces open the same native picker: the Drive card (choosing
// documents for the Owl to read) and the Content Studio asset library (importing
// images/clips). Each passes its OWN token fetcher, because the permission that
// grants a picker differs per surface — integrations.manage for the Drive card,
// campaigns.approve for a Studio import — while the picker itself is identical.
//
// The Picker browses as the CONNECTED Google account and, on the drive.file
// scope, picking is what GRANTS access: nothing is readable until chosen here.

const FOLDER_MIME = 'application/vnd.google-apps.folder';
// What the Owl's document reader can ingest (server/googleDrive.js kindOf).
// Folders included so they stay selectable — a folder is a valid synced source.
export const OWL_DOC_MIMES = [
  'application/vnd.google-apps.spreadsheet', 'application/vnd.google-apps.document',
  'application/vnd.google-apps.presentation', FOLDER_MIME,
  'text/csv', 'text/plain', 'text/markdown', 'application/pdf',
].join(',');

// What the Studio's library accepts (server/studio.js ASSET_MIMES is the real
// authority — this is the picker-side filter). The Picker takes explicit types,
// not wildcards, so the common ones are spelled out.
export const MEDIA_MIMES = [
  'image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/heic', 'image/heif', 'image/svg+xml',
  'video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v',
].join(',');

// Load Google's picker script once per page (idempotent — concurrent callers
// share the same in-flight promise rather than injecting duplicate <script>s).
let loading = null;
function loadPicker() {
  if (window.google?.picker) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://apis.google.com/js/api.js';
    s.onload = () => window.gapi.load('picker', { callback: resolve, onerror: reject });
    s.onerror = () => reject(new Error("Couldn't load the Google picker."));
    document.head.appendChild(s);
  }).catch((e) => { loading = null; throw e; });
  return loading;
}

// Open the picker and resolve with the chosen docs ([] when cancelled).
// `getToken` → { accessToken, apiKey } from whichever surface's route applies.
// `mode`: 'docs' (everything, folders selectable) | 'media' (images + video).
export async function pickFromDrive({ getToken, mode = 'docs', multiple = true, mimeTypes = '' }) {
  const t = await getToken();
  // Without a developerKey Google answers the picker iframe with a bare "403.
  // That's an error." — unreadable as a config problem. Say what's actually
  // missing, in the environment it's missing from.
  if (!t.apiKey) {
    throw new Error('Google’s picker key isn’t set for this environment yet — add the Picker API key in Admin → Integrations → Google, then try again.');
  }
  await loadPicker();
  const g = window.google.picker;
  return new Promise((resolve) => {
    const view = (ownedByMe) => {
      // Always a BROWSABLE view. The dedicated images-and-videos view is flat —
      // every picture in the account in one list, no folders — which is hopeless
      // on a real Drive where people find things by where they filed them.
      const v = new g.DocsView(g.ViewId.DOCS)
        .setIncludeFolders(true)
        // A folder can be a synced source for the Owl; it can't be a single
        // asset, so the Studio navigates folders without selecting them.
        .setSelectFolderEnabled(mode !== 'media');
      // Only offer what the caller can actually ingest — picking a file that will
      // just come back "unsupported" wastes the user's time and reads as a bug.
      // Folders must be in the list too, or filtering hides them and navigation
      // goes with them.
      const types = mimeTypes || (mode === 'media' ? MEDIA_MIMES : '');
      if (types) v.setMimeTypes([...new Set(`${types},${FOLDER_MIME}`.split(','))].join(','));
      return v.setOwnedByMe(ownedByMe);
    };
    let b = new g.PickerBuilder()
      // Be explicit about the host origin. Left to infer it, the picker can
      // answer "403 — you do not have access to this page" on an SPA route (and
      // always would inside an embed), which reads as a Google account problem.
      .setOrigin(window.location.origin)
      .setOAuthToken(t.accessToken)
      .addView(view(true))       // My Drive
      .addView(view(false))      // Shared with me
      .setCallback((data) => {
        if (data.action === g.Action.PICKED) resolve(data.docs || []);
        else if (data.action === g.Action.CANCEL) resolve([]);
      });
    if (multiple) b = b.enableFeature(g.Feature.MULTISELECT_ENABLED);
    b = b.setDeveloperKey(t.apiKey);
    if (t.appId) b = b.setAppId(t.appId); // ties picked files to this Cloud project (drive.file)
    b.build().setVisible(true);
  });
}
